import { useState, useEffect, useCallback } from "react";
import { Header } from "@/components/layout/Header";
import { useAuth } from "@/hooks/useAuth";
import { useModals } from "@/hooks/useModals";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Progress } from "@/components/ui/progress";
import { SessionList } from "@/components/backtest/SessionList";
import { BacktestResults, type BacktestAnalysis } from "@/components/backtest/BacktestResults";
import { DocumentUploadZone, type FileItem } from "@/components/backtest/DocumentUploadZone";
import {
  ChevronLeft,
  Loader2,
  Sparkles,
  BarChart3,
  FileText,
  Trash2,
  Building2,
} from "lucide-react";

interface StoredDoc {
  id: string;
  doc_type: string;
  file_name: string;
  file_url: string;
  extracted_data: Record<string, unknown> | null;
  created_at: string;
}

interface CompanyWithCounts {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  contract_count?: number;
  invoice_count?: number;
  has_results?: boolean;
}

export default function BacktestPage() {
  const { signOut, companyId, profile } = useAuth();
  const isAdmin = profile?.role === "DiscoRun";
  const modals = useModals();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Company state
  const [companies, setCompanies] = useState<CompanyWithCounts[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [activeCompanyName, setActiveCompanyName] = useState("");

  // Document state for active session
  const [storedDocs, setStoredDocs] = useState<StoredDoc[]>([]);
  const [newContracts, setNewContracts] = useState<FileItem[]>([]);
  const [newInvoices, setNewInvoices] = useState<FileItem[]>([]);

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<BacktestAnalysis | null>(null);

  // Load companies
  const loadCompanies = useCallback(async () => {
    const { data: companiesData } = await supabase
      .from("backtest_sessions")
      .select("*")
      .order("created_at", { ascending: false });

    if (!companiesData) return;

    const { data: docs } = await supabase.from("backtest_documents").select("session_id, doc_type");

    const { data: results } = await supabase.from("backtest_results").select("session_id");

    const resultSet = new Set(results?.map((r) => r.session_id));

    const enriched: CompanyWithCounts[] = companiesData.map((s) => {
      const companyDocs = docs?.filter((d) => d.session_id === s.id) ?? [];
      return {
        ...s,
        contract_count: companyDocs.filter((d) => d.doc_type === "contract").length,
        invoice_count: companyDocs.filter((d) => d.doc_type === "invoice").length,
        has_results: resultSet.has(s.id),
      };
    });

    setCompanies(enriched);
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Load company details
  const openCompany = useCallback(
    async (id: string) => {
      setActiveCompanyId(id);
      const company = companies.find((c) => c.id === id);
      setActiveCompanyName(company?.name ?? "");

      const { data: docs } = await supabase
        .from("backtest_documents")
        .select("*")
        .eq("session_id", id)
        .order("created_at", { ascending: true });

      setStoredDocs((docs as StoredDoc[]) ?? []);

      const { data: cachedResult } = await supabase
        .from("backtest_results")
        .select("analysis")
        .eq("session_id", id)
        .maybeSingle();

      setResult(cachedResult?.analysis as BacktestAnalysis | null);
      setNewContracts([]);
      setNewInvoices([]);
    },
    [companies]
  );

  // Auto-open the assigned company for company-scoped (non-admin) users
  useEffect(() => {
    if (!isAdmin && companyId && companies.length > 0 && !activeCompanyId) {
      openCompany(companyId);
    }
  }, [companyId, companies, isAdmin, activeCompanyId, openCompany]);

  const createCompany = async (name: string) => {
    const { data, error } = await supabase
      .from("backtest_sessions")
      .insert({ name })
      .select()
      .single();

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await loadCompanies();
    openCompany(data.id);
  };

  const deleteCompany = async (id: string) => {
    const { data: docs } = await supabase
      .from("backtest_documents")
      .select("file_url")
      .eq("session_id", id);

    if (docs?.length) {
      const paths = docs
        .map((d) => {
          const url = new URL(d.file_url);
          return url.pathname.split("/backtest-files/")[1];
        })
        .filter(Boolean);

      if (paths.length) {
        await supabase.storage.from("backtest-files").remove(paths);
      }
    }

    await supabase.from("backtest_sessions").delete().eq("id", id);
    if (activeCompanyId === id) {
      setActiveCompanyId(null);
      setResult(null);
      setStoredDocs([]);
    }
    loadCompanies();
  };

  const deleteDocument = async (docId: string, fileUrl: string) => {
    try {
      const url = new URL(fileUrl);
      const path = url.pathname.split("/backtest-files/")[1];
      if (path) await supabase.storage.from("backtest-files").remove([path]);
    } catch {
      /* ignore */
    }

    await supabase.from("backtest_documents").delete().eq("id", docId);
    setStoredDocs((prev) => prev.filter((d) => d.id !== docId));
    loadCompanies();
  };

  const uploadAndSaveFiles = async (files: FileItem[], docType: "contract" | "invoice") => {
    const uploaded: StoredDoc[] = [];
    for (const f of files) {
      const filePath = `${activeCompanyId}/${docType}s/${Date.now()}-${f.name}`;
      const { error: uploadError } = await supabase.storage
        .from("backtest-files")
        .upload(filePath, f.file, { contentType: "application/pdf" });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        continue;
      }

      const { data: urlData } = supabase.storage.from("backtest-files").getPublicUrl(filePath);

      const { data: doc, error: insertError } = await supabase
        .from("backtest_documents")
        .insert({
          session_id: activeCompanyId!,
          doc_type: docType,
          file_name: f.name,
          file_url: urlData.publicUrl,
        })
        .select()
        .single();

      if (!insertError && doc) {
        uploaded.push(doc as StoredDoc);
      }
    }
    return uploaded;
  };

  const runAnalysis = async () => {
    if (!activeCompanyId) return;

    const totalNew = newContracts.length + newInvoices.length;
    const totalStored = storedDocs.length;

    if (totalNew === 0 && totalStored === 0) {
      toast({
        title: "No files",
        description: "Upload at least one contract or invoice.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(totalNew > 0);
    setIsAnalyzing(true);

    try {
      // Upload new files first
      let newlyUploaded: StoredDoc[] = [];
      if (totalNew > 0) {
        const uploadedContracts = await uploadAndSaveFiles(newContracts, "contract");
        const uploadedInvoices = await uploadAndSaveFiles(newInvoices, "invoice");
        newlyUploaded = [...uploadedContracts, ...uploadedInvoices];
        setStoredDocs((prev) => [...prev, ...newlyUploaded]);
        setNewContracts([]);
        setNewInvoices([]);
        setIsUploading(false);
      }

      // Prepare payloads — new files as base64, stored as extracted data
      const allDocs = [...storedDocs, ...newlyUploaded];
      const storedContracts = allDocs
        .filter((d) => d.doc_type === "contract" && d.extracted_data)
        .map((d) => ({ fileName: d.file_name, ...d.extracted_data }));
      const storedInvoices = allDocs
        .filter((d) => d.doc_type === "invoice" && d.extracted_data)
        .map((d) => ({ fileName: d.file_name, ...d.extracted_data }));

      // New files that don't have extracted data yet need base64
      const contractsNeedingParsing = allDocs.filter(
        (d) => d.doc_type === "contract" && !d.extracted_data
      );
      const invoicesNeedingParsing = allDocs.filter(
        (d) => d.doc_type === "invoice" && !d.extracted_data
      );

      // For newly uploaded files, we still have the File objects for current upload
      // For previously stored files without extracted_data, we fetch from storage
      // For all docs without extracted_data, we download and send as base64
      const fetchBase64FromUrl = async (url: string): Promise<string> => {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      };

      const newContractPayloads = await Promise.all(
        contractsNeedingParsing.map(async (d) => ({
          fileName: d.file_name,
          base64: await fetchBase64FromUrl(d.file_url),
        }))
      );

      const newInvoicePayloads = await Promise.all(
        invoicesNeedingParsing.map(async (d) => ({
          fileName: d.file_name,
          base64: await fetchBase64FromUrl(d.file_url),
        }))
      );

      const { data, error } = await supabase.functions.invoke("backtest-analysis", {
        body: {
          contracts: newContractPayloads,
          invoices: newInvoicePayloads,
          storedContracts: storedContracts.length > 0 ? storedContracts : undefined,
          storedInvoices: storedInvoices.length > 0 ? storedInvoices : undefined,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      const analysis = data.analysis as BacktestAnalysis;
      setResult(analysis);

      // Cache results

      await supabase
        .from("backtest_results")
        .upsert(
          { session_id: activeCompanyId, analysis: analysis as any },
          { onConflict: "session_id" }
        );

      loadCompanies();
      toast({ title: "Analysis complete", description: "Backtest results are ready." });
    } catch (err) {
      console.error("Backtest error:", err);
      toast({
        title: "Analysis failed",
        description: err instanceof Error ? err.message : "Could not complete analysis.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
      setIsUploading(false);
    }
  };

  const storedContractDocs = storedDocs.filter((d) => d.doc_type === "contract");
  const storedInvoiceDocs = storedDocs.filter((d) => d.doc_type === "invoice");

  return (
    <div className="min-h-screen bg-background">
      <Header
        onShowAbout={() => modals.setShowAboutModal(true)}
        onShowDocumentation={() => modals.setShowDocumentationModal(true)}
        onShowFeedback={() => modals.setShowFeedbackModal(true)}
        onShowACHHistory={() => modals.setShowACHHistoryModal(true)}
        onSignOut={signOut}
      />

      <main className="w-full px-6 py-6 space-y-8">
        {/* No company assigned and not admin */}
        {profile !== null && !isAdmin && !companyId ? (
          <Card className="max-w-md mx-auto p-8 text-center">
            <Building2 className="w-10 h-10 mx-auto mb-4 text-muted-foreground opacity-40" />
            <h3 className="text-base font-semibold text-foreground mb-2">No company assigned</h3>
            <p className="text-sm text-muted-foreground">
              Contact your administrator to be assigned to a company.
            </p>
          </Card>
        ) : (
          <>
            {/* Page header */}
            <div className="flex items-center gap-3">
              {/* Show back button only for admins (company users have no "back" destination) */}
              {(isAdmin || !companyId) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground"
                  onClick={() =>
                    activeCompanyId
                      ? (() => {
                          setActiveCompanyId(null);
                          setResult(null);
                        })()
                      : navigate("/")
                  }
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                  {activeCompanyId ? "All Companies" : "Dashboard"}
                </Button>
              )}
              {(isAdmin || !companyId) && <div className="h-4 w-px bg-border" />}
              <div>
                <h2 className="text-2xl font-display font-bold text-foreground tracking-tight">
                  {activeCompanyId ? activeCompanyName : "Early Payment Backtest"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {activeCompanyId
                    ? "Upload documents and run analysis for this company"
                    : "Create companies to analyze early payment utilization and missed savings"}
                </p>
              </div>
            </div>

            {/* Company list OR active company */}
            {!activeCompanyId ? (
              <SessionList
                companies={companies}
                onSelect={openCompany}
                onCreate={createCompany}
                onDelete={deleteCompany}
              />
            ) : (
              <div className="space-y-6">
                {/* Upload new documents */}
                <Card className="p-5 border-primary/20 bg-primary/[0.02]">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">Add Documents</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Upload additional contracts or invoices, then re-run analysis to update
                        results.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <DocumentUploadZone
                      label="Contracts"
                      description="Supplier contracts with early payment / discount terms"
                      files={newContracts}
                      onAdd={(files) => setNewContracts((prev) => [...prev, ...files])}
                      onRemove={(i) =>
                        setNewContracts((prev) => prev.filter((_, idx) => idx !== i))
                      }
                    />
                    <DocumentUploadZone
                      label="Invoices"
                      description="Historical invoices to analyze payment patterns"
                      files={newInvoices}
                      onAdd={(files) => setNewInvoices((prev) => [...prev, ...files])}
                      onRemove={(i) => setNewInvoices((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  </div>

                  <div className="mt-5 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {storedDocs.length} stored + {newContracts.length + newInvoices.length} new
                      ready
                    </p>
                    <Button
                      onClick={runAnalysis}
                      disabled={
                        isAnalyzing ||
                        (storedDocs.length === 0 &&
                          newContracts.length === 0 &&
                          newInvoices.length === 0)
                      }
                      size="sm"
                    >
                      {isAnalyzing ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          {isUploading ? "Uploading…" : "Analyzing…"}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          {result ? "Re-run Analysis" : "Run Backtest"}
                        </>
                      )}
                    </Button>
                  </div>

                  {isAnalyzing && (
                    <div className="mt-4 space-y-2">
                      <Progress value={undefined} className="h-1.5" />
                      <p className="text-[11px] text-muted-foreground text-center">
                        {isUploading
                          ? "Uploading documents to storage…"
                          : "AI is analyzing your documents… this may take a moment"}
                      </p>
                    </div>
                  )}
                </Card>

                {/* Results */}
                {result && <BacktestResults result={result} />}

                {/* Uploaded documents - below results */}
                {storedDocs.length > 0 && (
                  <Card className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-3">
                      Uploaded Documents ({storedDocs.length})
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {storedContractDocs.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">
                            Contracts ({storedContractDocs.length})
                          </p>
                          <div className="space-y-1">
                            {storedContractDocs.map((d) => (
                              <div
                                key={d.id}
                                className="flex items-center justify-between text-xs bg-muted/50 rounded px-2.5 py-1.5"
                              >
                                <div className="flex items-center gap-1.5 truncate">
                                  <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate font-mono">{d.file_name}</span>
                                </div>
                                <button
                                  onClick={() => deleteDocument(d.id, d.file_url)}
                                  className="text-muted-foreground hover:text-destructive ml-2"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {storedInvoiceDocs.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">
                            Invoices ({storedInvoiceDocs.length})
                          </p>
                          <div className="space-y-1">
                            {storedInvoiceDocs.map((d) => (
                              <div
                                key={d.id}
                                className="flex items-center justify-between text-xs bg-muted/50 rounded px-2.5 py-1.5"
                              >
                                <div className="flex items-center gap-1.5 truncate">
                                  <FileText className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate font-mono">{d.file_name}</span>
                                </div>
                                <button
                                  onClick={() => deleteDocument(d.id, d.file_url)}
                                  className="text-muted-foreground hover:text-destructive ml-2"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
